import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowLeft,
  Download,
  Copy,
  QrCode,
  Palette,
  Square,
  Circle,
  ImageIcon,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const QR_SHAPES = [
  { id: 'square', label: 'Square', icon: Square },
  { id: 'rounded', label: 'Rounded', icon: Circle },
];

const QR_PRESETS = [
  { id: 'default', label: 'Classic', fg: '#000000', bg: '#FFFFFF' },
  { id: 'navy', label: 'Navy Blue', fg: '#16335E', bg: '#FFFFFF' },
  { id: 'teal', label: 'Teal', fg: '#0d9488', bg: '#FFFFFF' },
  { id: 'purple', label: 'Purple', fg: '#7c3aed', bg: '#FFFFFF' },
  { id: 'dark', label: 'Dark Mode', fg: '#FFFFFF', bg: '#1e293b' },
  { id: 'gradient-blue', label: 'Ocean', fg: '#0369a1', bg: '#f0f9ff' },
  { id: 'forest', label: 'Forest', fg: '#166534', bg: '#f0fdf4' },
  { id: 'sunset', label: 'Sunset', fg: '#ea580c', bg: '#fff7ed' },
];

const QRCustomizerPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState(null);
  const [generatingHD, setGeneratingHD] = useState(false);
  
  // QR Customization State
  const [qrConfig, setQrConfig] = useState({
    size: 300,
    fgColor: '#000000',
    bgColor: '#FFFFFF',
    shape: 'square',
    includeMargin: true,
    marginSize: 4,
    errorCorrectionLevel: 'H',
    includeLogo: false,
  });
  
  const [qrWithLogoUrl, setQrWithLogoUrl] = useState(null);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() });
        setCardData(response.data);
        if (response.data.qr_color) {
          setQrConfig(prev => ({ ...prev, fgColor: response.data.qr_color }));
        }
        if (response.data.qr_bg_color) {
          setQrConfig(prev => ({ ...prev, bgColor: response.data.qr_bg_color }));
        }
      } catch (error) {
        toast.error('Card not found');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const cardUrl = `${FRONTEND_URL}/card/${cardId}`;

  const handleConfigChange = (key, value) => {
    setQrConfig(prev => ({ ...prev, [key]: value }));
    if (key === 'includeLogo' && value && cardData?.profile_photo) {
      generateQRWithLogo();
    }
  };

  const applyPreset = (preset) => {
    setQrConfig(prev => ({
      ...prev,
      fgColor: preset.fg,
      bgColor: preset.bg,
    }));
    setQrWithLogoUrl(null);
  };

  const generateQRWithLogo = async () => {
    if (!cardData?.profile_photo) {
      toast.error('Please upload a profile photo first');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/cards/${cardId}/qr-base64?with_logo=true&color=${encodeURIComponent(qrConfig.fgColor)}&bg_color=${encodeURIComponent(qrConfig.bgColor)}&size=${qrConfig.size}`,
        { headers: getAuthHeaders() }
      );
      setQrWithLogoUrl(response.data.qr_code);
    } catch (error) {
      toast.error('Failed to generate QR with logo');
    }
  };

  const downloadQRAsHD = async (format = 'png') => {
    setGeneratingHD(true);
    
    try {
      // Generate high-resolution QR
      const hdSize = 1024;
      
      if (qrConfig.includeLogo && cardData?.profile_photo) {
        // Get HD QR with logo from backend
        const response = await axios.get(
          `${API_URL}/api/cards/${cardId}/qr-base64?with_logo=true&color=${encodeURIComponent(qrConfig.fgColor)}&bg_color=${encodeURIComponent(qrConfig.bgColor)}&size=${hdSize}`,
          { headers: getAuthHeaders() }
        );
        
        const link = document.createElement('a');
        link.download = `${cardData.name.replace(/\s+/g, '_')}_QR_HD.${format}`;
        link.href = response.data.qr_code;
        link.click();
        toast.success('HD QR code downloaded!');
      } else {
        // Generate from SVG
        const svg = document.getElementById('qr-customizer-svg');
        if (!svg) {
          toast.error('QR code not found');
          return;
        }
        
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          canvas.width = hdSize;
          canvas.height = hdSize;
          
          // Fill background
          ctx.fillStyle = qrConfig.bgColor;
          ctx.fillRect(0, 0, hdSize, hdSize);
          
          // Draw QR
          ctx.drawImage(img, 0, 0, hdSize, hdSize);
          
          const link = document.createElement('a');
          link.download = `${cardData.name.replace(/\s+/g, '_')}_QR_HD.${format}`;
          
          if (format === 'png') {
            link.href = canvas.toDataURL('image/png');
          } else if (format === 'jpeg') {
            link.href = canvas.toDataURL('image/jpeg', 0.95);
          }
          
          link.click();
          toast.success('HD QR code downloaded!');
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      }
    } catch (error) {
      toast.error('Failed to download HD QR');
    } finally {
      setGeneratingHD(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(cardUrl);
    toast.success('Link copied to clipboard');
  };

  const saveQRSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/cards/${cardId}`, {
        ...cardData,
        qr_color: qrConfig.fgColor,
        qr_bg_color: qrConfig.bgColor,
      }, { headers: getAuthHeaders() });
      toast.success('QR settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/cards/${cardId}/edit`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">QR Code Customizer</h1>
            <p className="text-muted-foreground">Customize your QR code with advanced options</p>
          </div>
          <Button onClick={saveQRSettings} className="gap-2" data-testid="save-qr-btn">
            Save Settings
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Controls */}
          <div className="space-y-6">
            {/* Color Presets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Quick Presets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {QR_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => applyPreset(preset)}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                        qrConfig.fgColor === preset.fg && qrConfig.bgColor === preset.bg
                          ? 'border-[#16335E] ring-2 ring-[#16335E]/20'
                          : 'border-transparent hover:border-muted'
                      }`}
                      style={{ backgroundColor: preset.bg }}
                      data-testid={`preset-${preset.id}`}
                    >
                      <div
                        className="w-8 h-8 rounded mx-auto mb-1"
                        style={{ backgroundColor: preset.fg }}
                      />
                      <p className="text-xs font-medium text-center" style={{ color: preset.fg }}>
                        {preset.label}
                      </p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custom Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Custom Colors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Foreground Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={qrConfig.fgColor}
                        onChange={(e) => handleConfigChange('fgColor', e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        data-testid="fg-color-input"
                      />
                      <Input
                        value={qrConfig.fgColor}
                        onChange={(e) => handleConfigChange('fgColor', e.target.value)}
                        className="font-mono text-sm"
                        data-testid="fg-color-text"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={qrConfig.bgColor}
                        onChange={(e) => handleConfigChange('bgColor', e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        data-testid="bg-color-input"
                      />
                      <Input
                        value={qrConfig.bgColor}
                        onChange={(e) => handleConfigChange('bgColor', e.target.value)}
                        className="font-mono text-sm"
                        data-testid="bg-color-text"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Size Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Square className="w-5 h-5" />
                  Size & Shape
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Preview Size</Label>
                    <span className="text-sm font-mono text-muted-foreground">{qrConfig.size}px</span>
                  </div>
                  <Slider
                    value={[qrConfig.size]}
                    onValueChange={(value) => handleConfigChange('size', value[0])}
                    min={150}
                    max={400}
                    step={10}
                    data-testid="size-slider"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include Margin</Label>
                    <p className="text-sm text-muted-foreground">Add quiet zone around QR</p>
                  </div>
                  <Switch
                    checked={qrConfig.includeMargin}
                    onCheckedChange={(checked) => handleConfigChange('includeMargin', checked)}
                    data-testid="margin-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Logo Embedding */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Logo Embedding
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Embed Profile Photo</Label>
                    <p className="text-sm text-muted-foreground">Add your photo to QR center</p>
                  </div>
                  <Switch
                    checked={qrConfig.includeLogo}
                    onCheckedChange={(checked) => handleConfigChange('includeLogo', checked)}
                    disabled={!cardData?.profile_photo}
                    data-testid="logo-toggle"
                  />
                </div>
                {!cardData?.profile_photo && (
                  <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
                    Upload a profile photo in the card editor to enable logo embedding.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Preview
                </CardTitle>
                <CardDescription>
                  Real-time preview of your QR code
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-6">
                <div 
                  className="p-8 rounded-2xl shadow-lg transition-all duration-300"
                  style={{ backgroundColor: qrConfig.bgColor }}
                >
                  {qrConfig.includeLogo && qrWithLogoUrl ? (
                    <img 
                      src={qrWithLogoUrl} 
                      alt="QR Code with Logo" 
                      style={{ width: qrConfig.size, height: qrConfig.size }}
                      data-testid="qr-preview-logo"
                    />
                  ) : (
                    <QRCodeSVG
                      id="qr-customizer-svg"
                      value={cardUrl}
                      size={qrConfig.size}
                      fgColor={qrConfig.fgColor}
                      bgColor={qrConfig.bgColor}
                      level={qrConfig.errorCorrectionLevel}
                      includeMargin={qrConfig.includeMargin}
                    />
                  )}
                </div>

                <div className="w-full p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Card URL:</p>
                  <code className="text-xs break-all">{cardUrl}</code>
                </div>
              </CardContent>
            </Card>

            {/* Download Options */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Download
                </CardTitle>
                <CardDescription>
                  Download your QR code in HD quality
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    onClick={() => downloadQRAsHD('png')}
                    disabled={generatingHD}
                    className="gap-2 bg-[#16335E] hover:bg-[#0A1A34]"
                    data-testid="download-png-btn"
                  >
                    {generatingHD ? (
                      <div className="loading-spinner w-4 h-4" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    PNG (1024px)
                  </Button>
                  <Button 
                    onClick={() => downloadQRAsHD('jpeg')}
                    disabled={generatingHD}
                    variant="outline"
                    className="gap-2"
                    data-testid="download-jpeg-btn"
                  >
                    {generatingHD ? (
                      <div className="loading-spinner w-4 h-4" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    JPEG (1024px)
                  </Button>
                </div>

                <Separator />

                <Button 
                  variant="outline" 
                  onClick={copyLink} 
                  className="w-full gap-2"
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy Card Link
                </Button>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-muted/50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-3">Tips for Best Results</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Ensure high contrast between foreground and background</li>
                  <li>• Test scanning with multiple devices before printing</li>
                  <li>• HD downloads (1024px) are ideal for print materials</li>
                  <li>• Logo embedding uses high error correction for reliability</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QRCustomizerPage;
